
import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, forkJoin, of, tap } from 'rxjs';
import { KeyTitleModel } from 'src/app/models/key-value-model';
import { BusinessMenu } from 'src/app/models/register-business';
import { LoaderService } from 'src/app/services/loader.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
})

export class MenuComponent {
  @ViewChild('menuModal') menuModalTemplate!: TemplateRef<MenuComponent>;
  foodPreviewAdded = false;
  foodPreviewUrl: string = "";
  typeSelect: any;
  dish: BusinessMenu = new BusinessMenu();
  @Input()
  types: KeyTitleModel[] = [];
  formData: FormData;
  file: File;
  private _id: number;
  @Input()
  set id(value: number) {
    this._id = value;
  }
  get id(): number {
    return this._id;
  }
  @Input()
  menuCount: number;
  @Output()
  menuAdded = new EventEmitter<BusinessMenu>();
  @Output()
  menuUpdated = new EventEmitter<any>();
  @Output() saveMenuSuccess = new EventEmitter<void>();
  @Input() businessPartnerId: number;
  isConfirmCategory: boolean = false;
  isInvalidCategory: boolean = false;
  categoryMapping: any[] = [];
  addCategory: string = 'api/inventory/productcategories';
  mappedCategory: string = 'api/tenants/membershipApp/category/mapping';
  modalRef: NgbModalRef;

  constructor(
    private userService: UserService,
    private toastr: ToastrService,
    private loaderService: LoaderService
  ) {
  }
  setDishValues(product: BusinessMenu): void {
    this.dish = product;
    this.formGroup.patchValue({
      Title: this.dish.title,
      Price: this.dish.price,
      Description: this.dish.description,
    });
    let selectedType = this.types.find(
      (item) => item.id === this.dish.productCategoryId
    );
    this.typeSelect = selectedType ?? null;
    if (this.dish.productImageUrl != null) {
      this.foodPreviewAdded = true;
      this.foodPreviewUrl = this.dish.productImageUrl;
      this.dish.productImageId = this.dish.productImageId;
    }
  }

  formGroup = new FormGroup({
    Title: new FormControl('', Validators.required),
    Price: new FormControl('', Validators.required),
    Description: new FormControl(''),
  });

  fillProductDetails(id: number) {
    this.loaderService.setLoading(true);
    this.userService
      .getById(`api/inventory/products/details`, id)
      .pipe(
        catchError((error) => {
          if (error.status == 404) {
            throw error;
          } else {
            this.loaderService.setLoading(false);
            throw error;
          }
        })
      )
      .subscribe((response: any) => {
        this.dish = response;
        this.dish.productImageId = response.productImages[0]?.id;
        this.formGroup.patchValue({
          Title: response.title,
          Price: response.price,
          Description: response.description,
        });
        let selectedType = this.types.find(
          (item) => item.id === response.productCategoryId
        );
        this.typeSelect = selectedType ?? null;
        if (response.productImages.length > 0) {
          this.foodPreviewAdded = true;
          this.foodPreviewUrl = response.productImages[0].smallImage;
        }
        this.loaderService.setLoading(false);
      });
  }

  sortItems(items: any[]): any[] {
    return items.slice().sort((item1, item2) => {
      if (item1.title.toLowerCase() < item2.title.toLowerCase()) {
        return -1;
      }
      if (item1.title.toLowerCase() > item2.title.toLowerCase()) {
        return 1;
      }
      return 0;
    });
  }

  onFileSelected(event: any) {
    this.foodPreviewAdded = true;
    this.file = event.target.files[0];
    if (!this.file.type.startsWith('image/')) {
      this.toastr.error('Please select image.');
      this.foodPreviewAdded = false;
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.foodPreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(this.file);
    this.formData = new FormData();
    this.formData.append('file', this.file);
  }

  fromGroupTouched(isSubmit: boolean = false) {
    if (this.formGroup.touched || isSubmit) {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    }
  }

  resetForm() {
    this.formGroup.reset();
    this.typeSelect = null;
    this.foodPreviewUrl = "";
    this.foodPreviewAdded = false;
    this.isInvalidCategory = false;
    this.saveMenuSuccess.emit();
    this.formGroup.updateValueAndValidity();
  }

  deleteImage() {

    if (this.dish.id != 0 && this.dish.productImageId != 0) {
      this.loaderService.setLoading(true);
      this.userService.delete(`api/bliss/blissProduct/removeImage/${this.dish.id}`, this.dish.productImageId)
        .pipe(
          catchError((error) => {
            if (error.status == 404) {
              throw error;
            } else {
              this.loaderService.setLoading(false);
              throw error;
            }
          })
        )
        .subscribe((response) => {
          this.foodPreviewAdded = false;
          this.foodPreviewUrl = '';
          this.dish.productImageUrl = '';
          this.toastr.success('Dish Image deleted successfully!');
          this.loaderService.setLoading(false);
        });
    }
    else {
      this.foodPreviewAdded = false;
      this.foodPreviewUrl = '';
    }
    return;
  }

  addDish(productId?: number) {
    this.fromGroupTouched(true);
    if (this.formGroup.invalid || this.typeSelect == undefined) {
      this.toastr.error("One or more fields has validation errors.");
      return;
    }
    else {
      if (this.typeSelect.label != undefined) {
        this.getMappedCategoryId(this.typeSelect.label, productId);
      } else {
        this.uploadData(productId, this.typeSelect.id);
      }
    }
  }

  uploadData(productId?: number, catId?: number) {
    if (productId != 0 && productId != undefined) {
      this.loaderService.setLoading(true);
      this.dish.id = productId;
      const Id = localStorage.getItem('branchId');
      this.dish.title = this.formGroup.get('Title').value;
      this.dish.description = this.formGroup.get('Description').value;
      this.dish.price = this.formGroup.get('Price').value;
      if (catId == 0) {
        const categoryId = this.typeSelect != null && this.typeSelect != undefined
          ? this.typeSelect.id
          : null;
        this.dish.productCategory = { Id: categoryId };
      } else {
        this.dish.productCategory = { Id: catId };
      }
      this.dish.productBranchMappings = [
        {
          branchId: Id,
          branch: {
            Id: Id,
          },
        },
      ];
      this.dish.productModifierMappings = [];
      this.userService
        .update(`api/inventory/products`, productId, this.dish)
        .pipe(
          catchError((error) => {
            if (error.status == 404) {
              this.resetForm();
              throw error;
            } else {
              this.loaderService.setLoading(false);
              this.resetForm();
              throw error;
            }
          })
        )
        .subscribe((response: any) => {
          this.dish = Object.assign({}, response);
          this.menuUpdated.emit(this.dish);
          if (this.foodPreviewUrl != "") {
            if (response.productImages.length > 0) {
              if (this.foodPreviewUrl != response.productImages[0].smallImage) {
                this.updateDishImage(response.productImages[0].id);
              }
            }
            else {
              this.uploadDishImage(productId);
            }
          }
          this.toastr.success('Dish updated successfully!');
          this.loaderService.setLoading(false);
          this.resetForm();
        });
    } else {
      this.loaderService.setLoading(true);
      const Id = localStorage.getItem('branchId');
      this.dish.title = this.formGroup.get('Title').value;
      this.dish.description = this.formGroup.get('Description').value;
      this.dish.price = this.formGroup.get('Price').value;
      if (catId == 0) {
        const categoryId = this.typeSelect != null && this.typeSelect != undefined
          ? this.typeSelect.id
          : null;
        this.dish.productCategory = { Id: categoryId };
      } else {
        this.dish.productCategory = { Id: catId };
      }
      this.dish.productBranchMappings = [
        {
          branchId: Id,
          branch: {
            Id: Id,
          },
        },
      ];
      this.dish.productModifierMappings = [];
      this.userService
        .create(`api/inventory/products`, this.dish)
        .pipe(
          catchError((error) => {
            if (error.status == 404) {
              this.resetForm();
              throw error;
            } else {
              this.loaderService.setLoading(false);
              this.resetForm();
              throw error;
            }
          })
        )
        .subscribe((response) => {
          this.dish = Object.assign({}, response);
          this.menuAdded.emit(this.dish);
          if (this.foodPreviewUrl != "" && this.dish.id) {
            this.uploadDishImage(this.dish.id);
          }
          this.toastr.success('Dish Added successfully!');
          this.loaderService.setLoading(false);
          this.resetForm();
        });
    }
  }

  confirmMapping = (productId?: number) => {
    const requests = [];
    for (let category of this.categoryMapping) {
      const categoryData = {
        title: category.yourCategory,
        visibleCategoryId: category.mappedCategory.id,
        isForMembership: true,
        businessPartnerId: this.businessPartnerId,
        isMainCategory: false
      };
      const request = this.userService.create(`${this.addCategory}`, categoryData).pipe(
        tap(() => {
          this.toastr.success(`Category ${category.yourCategory} added successfully`);
        }),
        catchError(() => {
          this.toastr.error(`Category ${category.yourCategory} does not add successfully.`);
          return of(null);
        })
      );
      requests.push(request);
    }

    forkJoin(requests).subscribe((response) => {
      const categoryId = response[0].id;
      this.uploadData(productId, categoryId);
    });
  }

  uploadDishImage(productId: number) {
    this.loaderService.setLoading(true);
    this.userService
      .create(
        `api/bliss/blissProduct/uploadImage/add/${productId}`,
        this.formData
      )
      .pipe(
        catchError((error) => {
          if (error.status == 404) {
            this.isInvalidCategory = false;
            throw error;
          } else {
            this.isInvalidCategory = false;
            this.loaderService.setLoading(false);
            throw error;
          }
        })
      )
      .subscribe((response) => {
        this.dish.productImageUrl = URL.createObjectURL(this.file);
        this.loaderService.setLoading(false);
        this.isInvalidCategory = false;
        this.saveMenuSuccess.emit();
      });
  }

  updateDishImage(imageId: number) {
    this.loaderService.setLoading(true);
    this.userService
      .create(
        `api/bliss/blissProduct/updateImage/${imageId}`,
        this.formData
      )
      .pipe(
        catchError((error) => {
          if (error.status == 404) {
            this.isInvalidCategory = false;
            throw error;
          } else {
            this.isInvalidCategory = false;
            this.loaderService.setLoading(false);
            throw error;
          }
        })
      )
      .subscribe((response) => {
        this.dish.productImageUrl = URL.createObjectURL(this.file);
        this.loaderService.setLoading(false);
        this.isInvalidCategory = false;
        this.saveMenuSuccess.emit();
      });
  }

  getMappedCategoryId = (category: string, productId?: number) => {
    this.userService.getAll(`${this.mappedCategory}?businessPartnerId=${this.businessPartnerId}&category=${category}`).subscribe((response: any) => {
      if (response == 0) {
        this.categoryMapping = [{ yourCategory: this.typeSelect.label, mappedCategory: this.types[0] }];
        this.isInvalidCategory = true;
      } else {
        this.uploadData(productId, response);
      }
    });
  }
}
